import { useEffect } from 'react';
import { UseLevelProgressionProps, LevelProgressionReturn } from './useLevelProgression.types';
import { useLevelProgressionMutations } from './mutations/useLevelProgressionMutations';
import { useLevelProgressionState } from './progression/useLevelProgressionState';
import { useLevelNavigation } from './progression/useLevelNavigation';
import { useLevelActivityTracking } from './progression/useLevelActivityTracking';
import { completeLessonAndAwardXP } from './progression/useLessonCompletionHandler';

// Import our new modularized hooks
import { useWordRecycling } from './level/useWordRecycling';
import { useLevelCompletion } from './level/useLevelCompletion';
import { usePerfectTracking } from './level/usePerfectTracking';
// Import PerfectLevelContext hook
import { usePerfectLevel } from '../contexts/PerfectLevelContext';
// Import Daily Quest progression hook
import { useDailyQuestProgression } from './progression/useDailyQuestProgression';

/**
 * Central hook for level progression management
 * Orchestrates level completion, navigation, and XP tracking
 */
export const useLevelProgression = ({
  userId,
  levels,
  setLevels,
  currentLevelIndex,
  setCurrentLevelIndex,
  totalXpGained,
  setTotalXpGained,
  selectedLesson,
  setShowLevelComplete,
  setShowLessonComplete,
  setShowLevelIntro,
  setExercises,
  currentExerciseIndex,
  setCurrentExerciseIndex,
  setShowAnswer,
  setUserInput,
  setIsInputCorrect,
  setWordsToReview,
  setTotalOriginalExercises,
  handleImperfectLesson,
}: UseLevelProgressionProps): LevelProgressionReturn => {
  
  // Get current level ID if available
  const currentLevel = levels[currentLevelIndex];
  const currentLevelId = currentLevel?.id;
  
  // Fetch state from sub-hooks
  const {
    isPerfectLevel,
    setIsPerfectLevel,
    isPerfectSpelling,
    setIsPerfectSpelling,
    previouslyCompletedLevels,
    setPreviouslyCompletedLevels,
    levelCompletionCounts,
    setLevelCompletionCounts,
    practiceMode,
    setPracticeMode
  } = useLevelProgressionState();
  
  // Access the PerfectLevelContext
  const { allLevelsPerfect, markLevelImperfect, getLevelPerfectStatus } = usePerfectLevel();
  
  // Initialize API mutation hooks
  const mutations = useLevelProgressionMutations({
    userId,
    selectedLesson
  });
  
  // Reset exercise state function to pass to other hooks
  const resetExerciseState = (generatedExercises: any[]) => {
    setExercises(generatedExercises);
    setTotalOriginalExercises(generatedExercises.length);
    setCurrentExerciseIndex(0);
    setShowAnswer(false);
    setUserInput('');
    setIsInputCorrect(null);
    setWordsToReview([]);
    
    // Reset both React state and global flags when starting a new level
    setIsPerfectLevel(true);
    
    // Reset global flags used for tracking perfect status
    (window as any).__PERFECT_LEVEL_STATUS__ = true;
    (window as any).__HAD_RECYCLED_WORDS__ = false;
    console.debug(`⭐️ Starting new level with clean perfect tracking - isPerfectLevel=true, hadRecycledWords=false`);
  };
  
  // Initialize level navigation hooks
  const {
    moveToNextLevel,
    skipToNextLevel,
    restartCurrentLevel
  } = useLevelNavigation({
    userId,
    levels,
    setLevels,
    currentLevelIndex,
    setCurrentLevelIndex,
    totalXpGained,
    selectedLesson,
    setShowLevelIntro,
    setShowLevelComplete,
    setShowLessonComplete,
    setExercises,
    resetExerciseState,
    setPracticeMode,
    mutations,
    handleImperfectLesson
  });
  
  // Initialize level activity tracking with access to the resetLessonCheck method
  const { 
    checkPreviouslyCompletedLevels,
    resetLessonCheck
  } = useLevelActivityTracking({
    userId,
    selectedLesson,
    mutations,
    setPreviouslyCompletedLevels,
    setLevelCompletionCounts
  });
  
  // Create a default empty array for initial state
  const emptyExercises: any[] = [];
  const emptyWordsToReview: string[] = [];
  
  // Initialize word recycling functionality with proper variable handling
  const { recycleFailedWords } = useWordRecycling({
    wordsToReview: emptyWordsToReview,
    setWordsToReview,
    setIsPerfectLevel,
    setShowLevelComplete,
    currentLevelId
  });
  
  // Initialize level completion functionality
  const { completeLevelHandler } = useLevelCompletion({
    userId,
    levels,
    setLevels,
    currentLevelIndex,
    totalXpGained,
    setTotalXpGained,
    selectedLesson,
    setShowLevelComplete,
    setIsPerfectLevel,
    levelCompletionCounts,
    setLevelCompletionCounts,
    mutations,
    practiceMode,
    handleImperfectLesson
  });
  
  // Initialize perfect tracking functionality
  const { handleImperfectLevel } = usePerfectTracking({
    selectedLesson,
    currentLevelIndex,
    currentLevelId,
    setIsPerfectLevel,
    handleImperfectLesson
  });
  
  /**
   * Completes the current level and handles state updates
   */
  const completeLevel = (wordsToReview: string[] = []) => {
    if (!levels[currentLevelIndex] || !selectedLesson) return;
    
    // CRITICAL FIX: Handle word recycling if there are words to review
    // but ensure activity logging still happens
    if (wordsToReview.length > 0) {
      // Process the failed words but still continue with level completion
      // to ensure activity logging happens
      console.log(`📊 [completeLevel] Processing ${wordsToReview.length} failed words before completion`);
      
      // Prepare the level for completion
      recycleFailedWords(wordsToReview);
      
      // IMPORTANT: Continue with normal level completion to ensure activity is logged
      console.log(`📊 [completeLevel] Continuing to completeLevelHandler for activity logging`);
      completeLevelHandler(wordsToReview);
      return;
    }
    
    // Otherwise complete the level normally
    completeLevelHandler(wordsToReview);
  };
  
  // Only check activity logs on mount or when lesson changes, not on every render
  useEffect(() => {
    if (selectedLesson && userId) {
      // Add a small delay to avoid blocking rendering
      const timer = setTimeout(() => {
        checkPreviouslyCompletedLevels();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [selectedLesson?.id, userId]); // Depend on selectedLesson.id instead of the whole object
  
  // Debug current state values
  useEffect(() => {
    console.debug(`[useLevelProgression] Current state: isPerfectLevel=${isPerfectLevel}`);
  }, [isPerfectLevel]);

  return {
    completeLevel,
    moveToNextLevel,
    skipToNextLevel,
    completeLessonAndAwardXP: async () => {
      // Access the current perfect lesson status
      const lessonId = selectedLesson?.id;
      
      // CRITICAL FIX: Use the PerfectLevelContext to determine if all levels were perfect
      // This ensures we're using the authoritative source for perfect level tracking
      const allLevelsArePerfect = allLevelsPerfect();
      
      // Integrate with the legacy flags for backward compatibility
      let isPerfectLessonStatus = allLevelsArePerfect;
      const legacyPerfectStatus = (window as any).__PERFECT_LESSON__ === true;
      
      // Double-check with localStorage if possible
      let localStoragePerfectStatus = true; // Default to true
      if (lessonId) {
        try {
          const storedValue = localStorage.getItem(`lesson_${lessonId}_perfect`) === 'true';
          localStoragePerfectStatus = storedValue;
          console.debug(`🏆 [completeLessonAndAwardXP] Reading perfect lesson status from localStorage: ${storedValue}`);
        } catch (e) {
          console.error('Error reading localStorage:', e);
        }
      }
      
      // Ensure we take the most conservative approach - a lesson is only perfect if all sources agree
      // If ANY source says it's imperfect, we consider it imperfect
      const finalPerfectStatus = allLevelsArePerfect && localStoragePerfectStatus && legacyPerfectStatus;
      
      // Log detailed debugging information for all sources
      console.debug(`🏆 [completeLessonAndAwardXP] Perfect lesson sources: context=${allLevelsArePerfect}, localStorage=${localStoragePerfectStatus}, legacy=${legacyPerfectStatus}`);
      console.debug(`🏆 [completeLessonAndAwardXP] Final perfect lesson status: ${finalPerfectStatus}`);
      
      // Set the global flag for other components
      (window as any).__PERFECT_LESSON__ = finalPerfectStatus;
      try {
        if (lessonId) {
          localStorage.setItem(`lesson_${lessonId}_perfect`, String(finalPerfectStatus));
        }
      } catch (e) {
        console.error('Error setting localStorage:', e);
      }
      
      // CRITICAL FIX: Function to handle lesson bonus calculation - ensure it ALWAYS returns 10 for perfect lessons
      const updateLessonBonus = (isPerfect: boolean): number => {
        const PERFECT_LESSON_BONUS = 10; // XP bonus for completing all levels perfectly
        const bonusAmount = isPerfect ? PERFECT_LESSON_BONUS : 0;
        console.debug(`🏆 [updateLessonBonus] Perfect=${isPerfect}, returning bonus: ${bonusAmount}`);
        return bonusAmount;
      };
      
      // Get the daily quest progression handler
      const { completeDailyQuest } = useDailyQuestProgression();
      
      // Check if this is a daily quest or a regular lesson
      if (selectedLesson?.isDailyQuest) {
        console.log(`🎯 [useLevelProgression] Completing daily quest: ${selectedLesson.name}`);
        
        try {
          // Call the daily quest completion handler and await the result
          const finalXpGained = await completeDailyQuest(
            userId,
            selectedLesson,
            totalXpGained,
            setShowLessonComplete,
            mutations,
            finalPerfectStatus,
            handleImperfectLesson,
            updateLessonBonus
          );
          
          // Update the totalXpGained state with the final value that includes any bonuses
          if (finalXpGained !== undefined && setTotalXpGained) {
            console.debug(`🎯 [completeDailyQuest] Updating totalXpGained state with final value: ${finalXpGained}`);
            setTotalXpGained(finalXpGained);
          }
          
          return finalXpGained;
        } catch (error) {
          console.error('Error completing daily quest:', error);
          return totalXpGained; // Return the existing XP if there was an error
        }
      } else {
        try {
          // Standard lesson completion logic for regular lessons
          const finalXpGained = await completeLessonAndAwardXP(
            userId,
            selectedLesson,
            totalXpGained,
            setShowLessonComplete,
            mutations,
            finalPerfectStatus, // Use our comprehensive perfect status determination
            handleImperfectLesson,
            updateLessonBonus
          );
          
          // Update the totalXpGained state with the final value that includes any bonuses
          if (finalXpGained !== undefined && setTotalXpGained) {
            console.debug(`🏆 [completeLessonAndAwardXP] Updating totalXpGained state with final value: ${finalXpGained}`);
            setTotalXpGained(finalXpGained);
          }
          
          return finalXpGained;
        } catch (error) {
          console.error('Error completing lesson:', error);
          return totalXpGained; // Return the existing XP if there was an error
        }
      }
    },
    isPerfectLevel, // Single source of truth for perfect level completion
    isPerfectSpelling, // Kept for backward compatibility with existing code
    handleImperfectLevel, // Function to mark a level as imperfect
    previouslyCompletedLevels,
    checkPreviouslyCompletedLevels,
    levelCompletionCounts,
    restartCurrentLevel,
    practiceMode,
    resetLessonCheck // Expose the reset method
  };
};
